import { getCallLogs } from '@/api/logs';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import IntakeFormComponent from './IntakeFormComponent';
import { Loading } from '@/components/Loading';
import { CallLog } from '@/interfaces/ILogs';
import { FaClipboardList, FaSort, FaChartBar } from 'react-icons/fa';
import isEmpty from '@/utils/isEmpty';
import { FormOutlined } from '@ant-design/icons';
import Header from '@/components/Header/Header';
import { safeJSONParse } from '@/utils/helper';

const IntakeForms = () => {
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const [startDate] = useState<string | null>(null);
	const [endDate] = useState<string | null>(null);
	const [callLogData, setcallLogData] = useState<CallLog[]>([]);
	const { data, isLoading } = useQuery({
		queryKey: ['call-logs'],
		queryFn: () => {
			return getCallLogs(selectedAgentId as string, { limit: null });
		},
	});
	const loggedInUser = useAppSelector(getLoggedInUser);

	const filterDataByDateRange = (data: any[], startDate: string | null, endDate: string | null) => {
		if (!startDate && !endDate) {
			return data;
		}

		return data.filter((row: any) => {
			const date = moment.utc(row['date']).tz(selectedAgent?.time_zone ?? 'UTC');
			return (
				(!startDate || date?.isSameOrAfter(startDate, 'day')) &&
				(!endDate || date.isSameOrBefore(endDate, 'day'))
			);
		});
	};

	useEffect(() => {
		if (data) {
			const callLogs = Array.isArray(data) ? data : data.callLogs;

			let callLogData =
				callLogs && callLogs.length > 0
					? [...callLogs].sort((a, b) => moment(a.date).unix() - moment(b.date).unix()).reverse()
					: [];
			callLogData = filterDataByDateRange(callLogData, startDate, endDate);
			setcallLogData(callLogData);
		}
	}, [data, startDate, endDate, selectedAgent?.time_zone]);

	const renderEmptyState = () => (
		<div className="bg-white min-h-screen w-full flex flex-col items-center justify-start p-4 sm:p-8 pt-8 sm:pt-16">
			<p className="text-lg sm:text-xl text-gray-600 mb-8 text-center max-w-2xl px-4">
				Our AI collects and organizes all your intake form responses in one place — automatically.
			</p>
			<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 mb-8 sm:mb-12 w-full max-w-4xl">
				<FeatureCard
					icon={<FaClipboardList className="text-3xl sm:text-4xl mb-2 sm:mb-4 text-blue-500" />}
					title="All Responses"
					description="Every form response, centralized"
				/>
				<FeatureCard
					icon={<FaSort className="text-3xl sm:text-4xl mb-2 sm:mb-4 text-purple-500" />}
					title="Smart Sorting"
					description="Find information quickly"
				/>
				<FeatureCard
					icon={<FaChartBar className="text-3xl sm:text-4xl mb-2 sm:mb-4 text-yellow-500" />}
					title="Insights"
					description="Get data-driven business insights"
				/>
			</div>
			<p className="text-base text-gray-600 text-center max-w-2xl px-4">
				Set up your forms in "Configure" to get started. Data will populate here automatically.
			</p>
		</div>
	);

	return (
		<div>
			<Header
				title="Intake Forms"
				description="All your form responses are collected here automatically"
				icon={<FormOutlined />}
				debrand={loggedInUser?.resellerClient}
			/>
			<div className="p-4">
				<div className="mb-10 w-full mx-auto text-center">
					<h1 className="text-3xl mb-4 text-center font-bold">Intake Forms</h1>
				</div>
				{isLoading ? (
					<div className="text-xl relative bottom-80">
						<Loading />
					</div>
				) : callLogData.some((log) => {
						const obj = safeJSONParse(log?.intakeResponses, []);
						return !isEmpty(obj);
				  }) ? (
					<IntakeFormComponent sortedData={callLogData} />
				) : (
					// renderEmptyState()
					renderEmptyState()
				)}
			</div>
		</div>
	);
};

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({
	icon,
	title,
	description,
}) => (
	<div className="border border-gray-200 rounded-lg p-4 sm:p-6 text-left w-full shadow-sm hover:shadow-md transition-shadow duration-300">
		{icon}
		<h3 className="text-lg sm:text-xl font-semibold mb-2 text-gray-800">{title}</h3>
		<p className="text-sm text-gray-600">{description}</p>
	</div>
);

export default IntakeForms;
